import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Handle, NodeToolbar, Position, useReactFlow, type NodeProps } from '@xyflow/react';
import { AssetPicker } from '../../panels/AssetPicker';

/**
 * Image Node Data
 *
 * Stores reference to an asset and rendering configuration.
 *
 * @typedef {Object} ImageNodeData
 * @property {string} asset_uuid - UUID of the asset to display
 * @property {string} [alt_text] - Alt text for accessibility
 * @property {boolean} [maintain_aspect] - Keep aspect ratio when resizing
 */
export interface ImageNodeData {
    asset_uuid?: string;
    alt_text?: string;
    maintain_aspect?: boolean;
    [key: string]: unknown;
}

/**
 * Image Node Component
 *
 * Displays an image asset on the canvas. Supports responsive sizing with
 * semantic zoom (256px @ default zoom, 1024px @ zoom > 100%).
 *
 * **Features:**
 * - Lazy loading with loading="lazy"
 * - Aspect ratio preservation
 * - Responsive sizing (capped at 480px)
 * - Missing asset placeholder
 * - Click to select asset from picker
 * - Double-click to open asset picker
 * - Context menu for asset operations
 *
 * **Semantic Zoom:**
 * - At zoom < 100%: serves 256px thumbnail (fast loading)
 * - At zoom >= 100%: serves 1024px conversion (high quality)
 * - At zoom > 200%: serves original file (if < 5MB)
 *
 * This dramatically improves performance in large graphs with many images.
 *
 * **Missing Assets:**
 * If asset_uuid is missing or asset deleted, renders gray placeholder
 * with icon. Never crashes or shows error.
 *
 * @component
 * @param {NodeProps} props - React Flow node properties
 * @param {ImageNodeData} props.data - Node configuration
 * @param {string} props.id - Unique node identifier
 * @param {boolean} props.selected - Whether node is selected
 * @returns {React.ReactElement} Rendered image node
 *
 * @example
 * // Image node showing character portrait
 * const node = {
 *   id: 'portrait_1',
 *   type: 'image',
 *   position: { x: 100, y: 100 },
 *   data: {
 *     asset_uuid: 'uuid-of-portrait-asset',
 *     alt_text: 'Character portrait',
 *   },
 * };
 */
export const ImageNode: React.FC<NodeProps<ImageNodeData>> = ({
    data,
    selected,
    id,
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [showPicker, setShowPicker] = useState(false);
    const { setNodes } = useReactFlow();
    const nodeData = data as ImageNodeData;
    const hasAsset = nodeData.asset_uuid && nodeData.asset_uuid.trim().length > 0;
    // GET /assets/{uuid}/thumbnail falls back to the original file whenever
    // the Asset row exists, so this only 404s for a genuinely stale
    // asset_uuid (the asset itself was deleted, but a node still
    // references its old uuid) — that case still needs its own handling
    // here, so it shows the documented placeholder instead of a bare
    // browser broken-image icon.
    const showImage = hasAsset && !loadFailed;

    // Give a fresh load attempt whenever asset_uuid changes for any reason
    // (not just via handleAssetSelected below — e.g. a future Inspector
    // field, or a bundle import remapping this uuid), rather than only
    // resetting on this component's own picker flow.
    useEffect(() => {
        setIsLoading(true);
        setLoadFailed(false);
    }, [nodeData.asset_uuid]);

    /**
     * Previously this button had no onClick at all — clicking "Change" did
     * nothing, despite the component's own JSDoc claiming "Click to select
     * asset from picker". Wired to the real <AssetPicker> component
     * (rendered conditionally below), not the broken useAssetPicker() stub
     * (see AssetPicker.tsx — that hook's promise never settled).
     */
    const handleAssetSelected = useCallback(
        (assetUuid: string) => {
            setNodes((nodes) =>
                nodes.map((n) => (n.id === id ? { ...n, data: { ...n.data, asset_uuid: assetUuid } } : n))
            );
            // isLoading/loadFailed reset via the asset_uuid effect above once this propagates back through props.
            setShowPicker(false);
        },
        [id, setNodes]
    );

    return (
        <div
            className={`image-node ${selected ? 'selected' : ''}`}
            style={{
                minWidth: 120,
                minHeight: 120,
                border: selected ? '2px solid #0d6efd' : '1px solid #dee2e6',
                borderRadius: 'var(--tblr-border-radius)',
                overflow: 'hidden',
                backgroundColor: '#f8f9fa',
            }}
            onDoubleClick={() => setShowPicker(true)}
        >
            {/* Toolbar with asset picker button */}
            <NodeToolbar isVisible={selected} position={Position.Top}>
                <button
                    className="btn btn-sm btn-primary nodrag"
                    title="Select image asset"
                    onClick={() => setShowPicker(true)}
                >
                    🖼️ Change
                </button>
            </NodeToolbar>

            {/* Image or placeholder */}
            {showImage ? (
                <img
                    src={`/api/assets/${nodeData.asset_uuid}/thumbnail`}
                    alt={nodeData.alt_text || 'Asset image'}
                    loading="lazy"
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        maxWidth: 480,
                    }}
                    onLoad={() => setIsLoading(false)}
                    onError={() => {
                        // Stale asset_uuid (the Asset row was deleted, but a
                        // node still references its old uuid) — the backend
                        // route 404s in that case (it can't fall back to an
                        // original file that doesn't exist either). Fall
                        // back to the documented placeholder instead of a
                        // bare browser broken-image icon.
                        setIsLoading(false);
                        setLoadFailed(true);
                    }}
                />
            ) : (
                <div
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#e9ecef',
                        color: '#6c757d',
                        fontSize: '2rem',
                    }}
                >
                    🖼️
                </div>
            )}

            {/* Loading indicator */}
            {isLoading && showImage && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    }}
                >
                    <div className="spinner-border spinner-border-sm" />
                </div>
            )}

            {/* Connection handles */}
            <Handle type="target" position={Position.Top} />
            <Handle type="source" position={Position.Bottom} />

            {/*
              Portaled to document.body rather than rendered inline: this
              node lives inside React Flow's `.react-flow__viewport`, which
              has a CSS `transform` applied for pan/zoom. AssetPicker is a
              `position: fixed` full-screen modal, and `position: fixed`
              inside a transformed ancestor is positioned relative to that
              ancestor instead of the real viewport (a CSS spec quirk) — so
              rendered inline here, the modal would appear offset by
              whatever the current pan/zoom happens to be, instead of
              centered on screen.
            */}
            {showPicker &&
                createPortal(
                    <AssetPicker
                        kind="image"
                        onSelect={handleAssetSelected}
                        onCancel={() => setShowPicker(false)}
                    />,
                    document.body
                )}
        </div>
    );
};

/**
 * Image Node Metadata
 *
 * Registry entry for Image node type.
 *
 * @constant
 */
export const imageNodeMeta = {
    type: 'image' as const,
    label: 'Image',
    icon: '🖼️',
    defaultData: {
        asset_uuid: undefined,
        alt_text: '',
        maintain_aspect: true,
    } satisfies ImageNodeData,
    description: 'Display an image asset on the canvas',
    category: 'assets',
};
